import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Loader2, CheckCircle2, XCircle, CalendarDays, Clock, Users, UserPlus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { toast } from 'sonner';

// Shareable self-registration page for a single small-group session slot.
// Wrapped in a ProtectedRoute (parent/customer), so the user is always
// authenticated by the time this renders.
const PublicSmallGroupRegister = () => {
  const { slotId } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [slot, setSlot] = useState(null);
  const [children, setChildren] = useState([]);
  const [selected, setSelected] = useState([]); // [{type:'player'|'user', id, name}]
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isParent = user?.role === 'parent';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const sRes = await api.get(`/small-group/slots/${slotId}/register-info`);
      setSlot(sRes.data);
      if (user?.role === 'parent') {
        const cRes = await api.get('/players/my-children').catch(() => ({ data: [] }));
        setChildren(cRes.data || []);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'This session could not be found.');
    } finally {
      setLoading(false);
    }
  }, [slotId, user]);

  useEffect(() => { load(); }, [load]);

  const toggle = (item) => {
    setSelected((prev) => {
      const exists = prev.some((p) => p.type === item.type && p.id === item.id);
      return exists ? prev.filter((p) => !(p.type === item.type && p.id === item.id)) : [...prev, item];
    });
  };

  const handleRegister = async () => {
    if (selected.length === 0) {
      toast.error('Please select at least one attendee.');
      return;
    }
    if (isParent && !terms) {
      toast.error('Please accept the Terms & Conditions to register.');
      return;
    }
    setSubmitting(true);
    const ok = [];
    const failed = [];
    for (const item of selected) {
      try {
        const payload = { slot_id: slotId, terms_accepted: true };
        if (item.type === 'user') payload.user_id = item.id;
        else payload.player_id = item.id;
        await api.post('/small-group/registrations', payload);
        ok.push(item.name);
      } catch (e) {
        failed.push(`${item.name}: ${e.response?.data?.detail || 'failed'}`);
      }
    }
    setSubmitting(false);
    if (ok.length) {
      setDone(true);
      toast.success(`Registered: ${ok.join(', ')}`);
    }
    if (failed.length) {
      toast.error(failed.join(' · '));
      if (!ok.length) return;
    }
  };

  const academy = slot?.academy_name || 'Academy';
  const full = slot && slot.available_spots <= 0;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-testid="sg-register-page">
      <Card className="w-full max-w-md shadow-xl border-slate-200">
        <CardContent className="p-8">
          <div className="mb-6">
            <p className="text-sm text-slate-500 leading-none">{academy}</p>
            <p className="text-lg font-semibold text-slate-900 mt-1">Session Registration</p>
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-10 text-slate-500" data-testid="sg-register-loading">
              <Loader2 className="h-6 w-6 animate-spin mb-3" />
              <p className="text-sm">Loading session…</p>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center text-center py-6" data-testid="sg-register-error">
              <XCircle className="h-12 w-12 text-rose-500 mb-3" />
              <h2 className="text-lg font-semibold text-slate-900">Unable to continue</h2>
              <p className="text-sm text-slate-500 mt-2">{error}</p>
            </div>
          )}

          {!loading && !error && done && (
            <div className="flex flex-col items-center text-center py-6" data-testid="sg-register-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-3" />
              <h2 className="text-xl font-semibold text-slate-900">You're registered!</h2>
              <p className="text-sm text-slate-500 mt-2">
                See you at {slot?.session_name}. The academy will share payment details separately.
              </p>
            </div>
          )}

          {!loading && !error && !done && slot && (
            <div data-testid="sg-register-ready">
              <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5">
                <p className="text-base font-semibold text-slate-900">{slot.session_name}</p>
                {slot.coach_name && <p className="text-xs text-slate-500 mt-0.5">Coach: {slot.coach_name}</p>}
                <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                  <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-slate-400" /> {slot.date}</p>
                  <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" /> {slot.start_time} – {slot.end_time}</p>
                  <p className="flex items-center gap-2"><Users className="h-4 w-4 text-slate-400" /> {slot.available_spots} spot{slot.available_spots === 1 ? '' : 's'} left</p>
                </div>
              </div>

              {full ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-3 text-sm text-amber-800 text-center" data-testid="sg-register-full">
                  This session is full. Please contact the academy.
                </div>
              ) : (
                <>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                    {isParent ? 'Who are you registering?' : 'Register yourself'}
                  </p>
                  <div className="space-y-2 mb-4">
                    {isParent && children.map((c) => {
                      const checked = selected.some((p) => p.type === 'player' && p.id === c.id);
                      return (
                        <label
                          key={c.id}
                          className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer ${checked ? 'border-violet-400 bg-violet-50' : 'border-slate-200 hover:bg-slate-50'}`}
                          data-testid={`sg-register-child-${c.id}`}
                        >
                          <Checkbox checked={checked} onCheckedChange={() => toggle({ type: 'player', id: c.id, name: c.full_name || c.name })} />
                          <span className="text-sm font-medium text-slate-800">{c.full_name || c.name}</span>
                        </label>
                      );
                    })}
                    {isParent && children.length === 0 && (
                      <p className="text-xs text-slate-500">No children on your account yet — you can still register yourself below.</p>
                    )}
                    {/* Self (adult) registration — available to both parents and customers */}
                    {(() => {
                      const checked = selected.some((p) => p.type === 'user' && p.id === user?.id);
                      return (
                        <label
                          className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer ${checked ? 'border-violet-400 bg-violet-50' : 'border-slate-200 hover:bg-slate-50'}`}
                          data-testid="sg-register-self"
                        >
                          <Checkbox checked={checked} onCheckedChange={() => toggle({ type: 'user', id: user?.id, name: `${user?.name || 'Myself'} (me)` })} />
                          <span className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                            <UserPlus className="h-4 w-4 text-slate-400" /> {user?.name ? `${user.name} (myself)` : 'Register myself'}
                          </span>
                        </label>
                      );
                    })()}
                  </div>

                  {isParent && (
                    <label className="flex items-start gap-2 mb-4 text-xs text-slate-600" data-testid="sg-register-terms">
                      <Checkbox checked={terms} onCheckedChange={(v) => setTerms(!!v)} className="mt-0.5" />
                      <span>I agree to the academy's Terms &amp; Conditions for coaching sessions.</span>
                    </label>
                  )}

                  <Button
                    className="w-full h-12 text-base bg-violet-600 hover:bg-violet-700"
                    onClick={handleRegister}
                    disabled={submitting || selected.length === 0}
                    data-testid="sg-register-submit"
                  >
                    {submitting ? (<><Loader2 className="h-5 w-5 animate-spin mr-2" /> Registering…</>) : (
                      <><UserPlus className="h-5 w-5 mr-2" /> Register {selected.length > 0 ? `${selected.length} ` : ''}attendee{selected.length === 1 ? '' : 's'}</>
                    )}
                  </Button>
                  <p className="text-center text-xs text-slate-400 mt-3">
                    Payment is handled by the academy after registration.
                  </p>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicSmallGroupRegister;
